import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Stack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    Card,
    Heading,
    LoadingIndicator,
    Text,
} from '@/components/v4';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { useQuery } from 'hooks/useCustomQuery';
import getService from 'API/Service/getService';
import createAddress from '@/API/Addresses/createAddress';
import { PlatformEnums } from '@/utils/platforms';
import EightByEightAddressPicker from 'pages/services/new/ServiceWizard/EightByEightCreate/EightByEightAddressPicker';
import {
    mapFormToAddressPayload,
    resolveCountryID,
} from 'pages/services/new/ServiceWizard/EightByEightCreate/addressHelpers';

export default function EightByEightServiceEdit() {
    const { id: serviceId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany, currentCompanyData } = useSelector(
        (state) => state.navigationLists,
    );
    const companyID = currentCompanyData?.id;
    const companyName = currentCompanyData?.name;

    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Address selection reported by the picker (auto-populates the company's
    // existing 8x8 platform address on load).
    const [addressSelection, setAddressSelection] = useState({
        effectiveAddress: {},
        existingAddressID: null,
        addressFilled: false,
        isManual: false,
        countries: [],
    });

    const { data: serviceObj, isLoading: serviceLoading } = useQuery({
        queryKey: ['8x8Service', serviceId],
        queryFn: async () => {
            const res = await getService(serviceId);
            return res.data;
        },
        enabled: !!serviceId,
        refetchOnWindowFocus: false,
    });

    const handleSave = async () => {
        const { effectiveAddress, existingAddressID, countries } =
            addressSelection;

        setSaveError(null);
        setIsSaving(true);
        try {
            // Only create when a new address was entered. A selected saved 8x8
            // address is already linked to the platform, so reuse it as-is.
            if (!existingAddressID) {
                await createAddress({
                    ...mapFormToAddressPayload(effectiveAddress, {
                        companyID,
                        companyName,
                        countryID: resolveCountryID(
                            effectiveAddress.country,
                            countries,
                        ),
                    }),
                    autoAddToNew: true,
                    addToPlatforms: [PlatformEnums.EIGHTBYEIGHT],
                });
            }

            await queryClient.invalidateQueries(['addresses', currentCompany]);
            toast.success('Platform address updated.');
            navigate(-1);
        } catch (e) {
            setSaveError(
                e?.response?.data?.title ??
                    e?.response?.data?.message ??
                    'Failed to update the platform address. Please try again.',
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (serviceLoading) return <LoadingIndicator />;

    return (
        <Box maxWidth="1064px" position="relative">
            <Breadcrumb sx={breadCrumbStyling}>
                <BreadcrumbItem>
                    <BreadcrumbLink
                        color={darkMode ? 'gray.300' : undefined}
                        onClick={() => navigate('/dashboard-services')}>
                        Dashboard
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink
                        color={darkMode ? 'whiteAlpha.600' : undefined}>
                        Edit
                    </BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            <Card
                borderRadius={15}
                p="30px"
                maxWidth="895px"
                ml={0}
                mt={2}
                bg={darkMode ? 'dark.tcap' : null}>
                <Heading
                    as="h3"
                    size="md"
                    mb={2}
                    color={darkMode ? 'white' : 'gray.700'}>
                    Edit {serviceObj?.name || '8×8'} Service
                </Heading>
                <Text
                    mb={6}
                    fontSize="sm"
                    color={darkMode ? 'gray.300' : 'gray.500'}>
                    Set or update the 8×8 platform address for this service.
                </Text>

                {saveError && (
                    <Alert status="error" variant="left-accent" mb={6}>
                        <AlertIcon />
                        <AlertDescription>{saveError}</AlertDescription>
                    </Alert>
                )}

                <EightByEightAddressPicker
                    darkMode={darkMode}
                    currentCompany={currentCompany}
                    onChange={setAddressSelection}
                />
            </Card>

            <Stack direction="row" spacing={3} mt={5}>
                <Button variant="outline" onClick={() => navigate(-1)}>
                    Back
                </Button>
                <Button
                    isDisabled={!addressSelection.addressFilled || isSaving}
                    isLoading={isSaving}
                    loadingText="Saving"
                    onClick={handleSave}>
                    Save
                </Button>
            </Stack>
        </Box>
    );
}
